import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Leaf, DollarSign, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [myOffers, setMyOffers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      toast.error('Please login to access dashboard');
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [offersRes, interestsRes] = await Promise.all([
        axios.get(`${API}/users/me/offers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users/me/interests`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMyOffers(offersRes.data);
      setInterests(interestsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;
  
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-zinc-400">Carregando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 md:px-12 lg:px-24">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Bem-vindo, <span className="text-primary">{user.name}</span>
            </h1>
            <p className="text-zinc-400 text-lg">Gerencie suas listagens de créditos de carbono e interesses</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-card border-border rounded-sm" data-testid="stat-my-offers">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-zinc-400 text-sm mb-1">Minhas Ofertas</div>
                    <div className="text-3xl font-mono font-bold">{myOffers.length}</div>
                  </div>
                  <Package className="w-12 h-12 text-primary" strokeWidth={1.5} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border rounded-sm" data-testid="stat-interested-in">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-zinc-400 text-sm mb-1">Interessado Em</div>
                    <div className="text-3xl font-mono font-bold">{interests.length}</div>
                  </div>
                  <Leaf className="w-12 h-12 text-primary" strokeWidth={1.5} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border rounded-sm" data-testid="stat-total-credits">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-zinc-400 text-sm mb-1">Total de Créditos</div>
                    <div className="text-3xl font-mono font-bold">
                      {myOffers.reduce((sum, offer) => sum + offer.carbon_credits, 0).toLocaleString()}
                    </div>
                  </div>
                  <DollarSign className="w-12 h-12 text-primary" strokeWidth={1.5} />
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="offers" className="space-y-6" data-testid="dashboard-tabs">
            <TabsList className="bg-card border border-border rounded-sm p-1">
              <TabsTrigger value="offers" className="rounded-sm" data-testid="tab-my-offers">
                Minha Oferta ({myOffers.length})
              </TabsTrigger>
              <TabsTrigger value="interests" className="rounded-sm" data-testid="tab-interests">
                Meus Interesses ({interests.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="offers" className="space-y-4">
              {myOffers.length === 0 ? (
                <Card className="bg-card border-border rounded-sm">
                  <CardContent className="p-12 text-center">
                    <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-xl font-heading font-bold mb-2">Nenhuma oferta ainda</h3>
                    <p className="text-zinc-400 mb-6">Comece criando sua primeira oferta de crédito de carbono</p>
                    <Link to="/create-offer">
                      <span className="text-primary hover:text-[#B3E600] transition-colors">Criar Oferta</span>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {myOffers.map((offer) => (
                    <Link key={offer.id} to={`/offers/${offer.id}`}>
                      <Card className="bg-card border border-border rounded-sm overflow-hidden hover:border-primary/50 transition-colors duration-300 h-full" data-testid={`my-offer-${offer.id}`}>
                        <CardHeader className="p-6 pb-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="rounded-full px-3 py-0.5 text-xs font-mono uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                              {offer.biome}
                            </Badge>
                            <Badge className="rounded-full px-3 py-0.5 text-xs font-mono uppercase tracking-wider bg-zinc-800 text-zinc-300 border border-zinc-700">
                              {offer.status}
                            </Badge>
                          </div>
                          <CardTitle className="text-lg line-clamp-2">{offer.description}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-6 pt-0 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Créditos</span>
                            <span className="font-mono font-bold text-primary">
                              {offer.carbon_credits.toLocaleString()} tCO₂
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Preço</span>
                            <span className="font-mono font-bold">${offer.price.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Área Florestal</span>
                            <span className="font-mono">{offer.forest_area.toLocaleString()} ha</span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="interests" className="space-y-4">
              {interests.length === 0 ? (
                <Card className="bg-card border-border rounded-sm">
                  <CardContent className="p-12 text-center">
                    <Leaf className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-xl font-heading font-bold mb-2">Nenhum interesse ainda</h3>
                    <p className="text-zinc-400 mb-6">Navegue pelo marketplace para encontrar créditos de carbono</p>
                    <Link to="/marketplace">
                      <span className="text-primary hover:text-[#B3E600] transition-colors">Explorar Marketplace</span>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {interests.map((offer) => (
                    <Link key={offer.id} to={`/offers/${offer.id}`}>
                      <Card className="bg-card border border-border rounded-sm overflow-hidden hover:border-primary/50 transition-colors duration-300 h-full" data-testid={`interest-offer-${offer.id}`}>
                        <CardHeader className="p-6 pb-4">
                          <Badge className="rounded-full px-3 py-0.5 text-xs font-mono uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 mb-2">
                            {offer.biome}
                          </Badge>
                          <CardTitle className="text-lg line-clamp-2">{offer.description}</CardTitle>
                          <p className="text-sm text-zinc-400">por {offer.seller_name}</p>
                        </CardHeader>
                        <CardContent className="p-6 pt-0 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Créditos</span>
                            <span className="font-mono font-bold text-primary">
                              {offer.carbon_credits.toLocaleString()} tCO₂
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Preço</span>
                            <span className="font-mono font-bold">${offer.price.toLocaleString()}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
